import { standaloneFetch } from '../../services/standaloneApi';
import type {
  EventChannel,
  SubTaskQuestionAnswer
} from '@dadohr/shared/domainTypes/experience';

/**
 * SC-32079 — Answer-question subtask submission.
 *
 * The answer payload is POSTed as a JSON body instead of being base64-encoded
 * into a GET `?d=` query param. Long free-text answers previously overflowed the
 * GET URL length ceiling (~8KB proxies / browser caps) and were rejected; a
 * request body has no such cap.
 *
 * The BE (`experiences_answerSubTasks`) accepts BOTH the POST body and the legacy
 * base64 GET param (dual-mode), so it is deployed ahead of this FE change.
 *
 * The Cloud Functions base URL is injected (not read from `env.json`) so this
 * module has ZERO runtime imports and is unit-testable without pulling in the
 * heavy `service.tsx` module graph (firebase, i18next, notistack, …).
 */

export type SubTaskAnswer = { subTaskId: string; d: SubTaskQuestionAnswer['d'] };

export type AnswerSubTasksPayload = {
  e: string; // experienceId
  t: string; // taskId
  c: EventChannel; // completion channel
  ds: { s: string; d: SubTaskQuestionAnswer['d'] }[]; // per-subtask answers
};

export type AnswerSubTasksApiResponse = {
  status?: string;
  completions?: {
    tasks: { taskId: string; taskTemplateId: string }[];
    subTasks: {
      taskId: string;
      taskTemplateId: string;
      subTaskId: string;
      subTaskTemplateId: string;
    }[];
  };
};

/**
 * The answer payload — the SAME shape whether the BE reads it from the POST JSON
 * body (current) or the legacy base64 GET `d` param, i.e. the two transports are
 * equivalent. `'APP'` is `EventChannel.APP` (a string enum); kept as a literal so
 * this module needs only a type-only import and stays trivially testable.
 */
export const buildAnswerSubTasksPayload = (
  experienceId: string,
  taskId: string,
  answers: SubTaskAnswer[]
): AnswerSubTasksPayload => ({
  e: experienceId,
  t: taskId,
  c: 'APP' as EventChannel,
  ds: answers.map((a) => ({ s: a.subTaskId, d: a.d }))
});

export const getAnswerSubTasksUrl = (cfBaseUrl: string): string => {
  const u = new URL(cfBaseUrl);
  u.pathname = '/experiences_answerSubTasks';
  return u.toString();
};

/**
 * POST the answers to the BE and return the parsed JSON response.
 */
export const postAnswerSubTasks = async (
  cfBaseUrl: string,
  experienceId: string,
  taskId: string,
  answers: SubTaskAnswer[]
): Promise<AnswerSubTasksApiResponse> => {
  const res = await standaloneFetch(getAnswerSubTasksUrl(cfBaseUrl), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(
      buildAnswerSubTasksPayload(experienceId, taskId, answers)
    )
  });
  return res.json();
};
